import type { PiiSpan } from "./types";
import { PII_TIERS } from "./ruleset/placeholders";

/**
 * Layer 1 of the engine: deterministic detection of formatted Danish
 * identifiers (CPR, phone, email, IBAN/account). These are `hard`-tier — high
 * precision AND recall, never suppressed by the whitelist. See the detection
 * spec in the plan.
 *
 * Each detector creates its own RegExp instance per call so there is no shared
 * `lastIndex` state between invocations.
 */

const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** Plausible DDMMYY date (Feb permissively allows 29). */
export function isValidCprDate(ddmmyy: string): boolean {
  const day = Number(ddmmyy.slice(0, 2));
  const month = Number(ddmmyy.slice(2, 4));
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > DAYS_IN_MONTH[month - 1]) return false;
  return true;
}

/**
 * Classic CPR modulus-11 check (weights 4 3 2 7 6 5 4 3 2 1).
 * Used ONLY as a soft confidence signal — never to reject a candidate: CPRs
 * issued since 1 Oct 2007 intentionally break mod-11, so rejecting on it would
 * miss real people. See plan detection spec.
 */
export function isCprMod11Valid(tenDigits: string): boolean {
  if (!/^\d{10}$/.test(tenDigits)) return false;
  const weights = [4, 3, 2, 7, 6, 5, 4, 3, 2, 1];
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += Number(tenDigits[i]) * weights[i];
  }
  return sum % 11 === 0;
}

/** ISO 7064 mod-97 check for an IBAN (already stripped of spaces, upper-cased). */
export function isValidIbanMod97(iban: string): boolean {
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const code = ch.charCodeAt(0);
    // Letters A-Z → 10..35; digits → their value.
    const value =
      code >= 65 && code <= 90
        ? code - 55
        : code >= 48 && code <= 57
          ? code - 48
          : NaN;
    if (Number.isNaN(value)) return false;
    remainder = (remainder * (value > 9 ? 100 : 10) + value) % 97;
  }
  return remainder === 1;
}

export function detectCpr(text: string): PiiSpan[] {
  // Separator between the DDMMYY and the 4-digit sequence: the canonical hyphen,
  // a space, or a non-breaking space (LAR-367 #4a — "010190 1234" was missed,
  // and it is not a phone either since DK subscriber numbers never start 0/1).
  // Deliberately ONE optional separator: "010190 - 1234" stays unmatched so
  // numeric ranges are not read as CPRs.
  const re = /(?<!\d)(\d{6})[-\u00A0 ]?(\d{4})(?!\d)/g;
  const spans: PiiSpan[] = [];
  for (const m of text.matchAll(re)) {
    const ddmmyy = m[1];
    if (!isValidCprDate(ddmmyy)) continue;
    const digits = m[1] + m[2];
    const start = m.index;
    const end = start + m[0].length;
    spans.push({
      start,
      end,
      text: m[0],
      type: "cpr",
      tier: PII_TIERS.cpr,
      source: "regex",
      confidence: isCprMod11Valid(digits) ? 0.98 : 0.85,
    });
  }
  return spans;
}

export function detectPhone(text: string): PiiSpan[] {
  // Optional +45 / 0045, then 8 digits in common groupings (2·4, 4·4, or plain).
  const re =
    /(?<!\d)(?:(?:\+45|0045)[ ]?)?(\d{2}(?:[ .\-]?\d{2}){3}|\d{4}[ .\-]?\d{4}|\d{8})(?!\d)/g;
  const spans: PiiSpan[] = [];
  for (const m of text.matchAll(re)) {
    const significant = m[1].replace(/\D/g, "");
    if (significant.length !== 8) continue;
    if (!/^[2-9]/.test(significant)) continue; // DK subscriber numbers start 2-9
    const start = m.index;
    const end = start + m[0].length;
    spans.push({
      start,
      end,
      text: m[0],
      type: "phone",
      tier: PII_TIERS.phone,
      source: "regex",
      confidence: m[0].includes("+45") || m[0].includes("0045") ? 0.9 : 0.8,
    });
  }
  return spans;
}

export function detectEmail(text: string): PiiSpan[] {
  const re = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
  const spans: PiiSpan[] = [];
  for (const m of text.matchAll(re)) {
    const start = m.index;
    const end = start + m[0].length;
    spans.push({
      start,
      end,
      text: m[0],
      type: "email",
      tier: PII_TIERS.email,
      source: "regex",
      confidence: 0.98,
    });
  }
  return spans;
}

export function detectAccount(text: string): PiiSpan[] {
  // Danish IBAN: DK + 2 check digits + 14 digits (spaces tolerated).
  const re = /(?<![A-Za-z0-9])DK\d{2}(?:[ ]?\d){14}(?![A-Za-z0-9])/gi;
  const spans: PiiSpan[] = [];
  for (const m of text.matchAll(re)) {
    const normalized = m[0].replace(/\s/g, "").toUpperCase();
    const start = m.index;
    const end = start + m[0].length;
    spans.push({
      start,
      end,
      text: m[0],
      type: "account",
      tier: PII_TIERS.account,
      source: "regex",
      confidence: isValidIbanMod97(normalized) ? 0.95 : 0.7,
    });
  }
  return spans;
}

/**
 * Run every structured (regex) detector and concatenate the raw spans.
 *
 * One cross-detector rule: a CPR candidate whose 4-digit tail is really the
 * first half of a grouped phone number ("010190 2233 4455") is dropped in
 * favour of the phone. Both readings cannot hold at once, and `resolveOverlaps`
 * keeps the earliest span — so keeping the CPR would suppress the phone and
 * leave its remaining digits ("4455") in the outgoing text, whereas the phone
 * reading covers every digit of the identifier and exposes only a bare date
 * (LAR-367 #4a; the hyphenated form had the same hole).
 */
export function detectStructured(text: string): PiiSpan[] {
  const phones = detectPhone(text);
  const crossesAPhone = (cpr: PiiSpan): boolean =>
    phones.some((p) => p.start > cpr.start && p.start < cpr.end && p.end > cpr.end);

  return [
    ...detectCpr(text).filter((cpr) => !crossesAPhone(cpr)),
    ...detectEmail(text),
    ...detectAccount(text),
    ...phones,
  ];
}
